import { useScheduleEnabled } from '@/hooks/useScheduleEnabled';
import { Schedule } from '@/pages/Appointments/Schedule/Schedule';
import type { ScheduleView } from '@/pages/Appointments/Schedule/scheduleEvents';
import {
  parseStoredScheduleView,
  resolveScheduleView,
  SCHEDULE_VIEW_STORAGE_KEY,
  ScheduleViewSearchParam,
} from '@/pages/Appointments/Schedule/scheduleViewPreference';
import { Spinner } from '@liza/ui/components/ui/spinner';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';

// The schedule view lives in the URL so it survives a reload and can be linked — "look at my Tuesday" is a sendable
// link. See `scheduleViewPreference` for why the param is optional rather than defaulted.
const AppointmentsSearchSchema = z.object({
  view: ScheduleViewSearchParam,
});

// Defense in depth: the nav item is hidden when `scheduleEnabled` is off, but a direct navigation
// to /appointments must also be blocked. Wait for LaunchDarkly to finish loading before redirecting
// so a clinician with the flag on isn't bounced during the initial flag load.
const AppointmentsRoute = (): React.ReactNode => {
  const { enabled, isReady } = useScheduleEnabled();
  // oxlint-disable-next-line no-use-before-define
  const { view: urlView } = Route.useSearch();
  // Two navigates with different shapes: the flag guard leaves the route entirely, while the view switch stays on it
  // and only rewrites `search` — which needs `from` for the search type to resolve.
  const navigate = useNavigate();
  // oxlint-disable-next-line no-use-before-define
  const navigateHere = useNavigate({ from: Route.fullPath });
  const [storedView, setStoredView] = useLocalStorage<ScheduleView | undefined>(SCHEDULE_VIEW_STORAGE_KEY, undefined, {
    deserializer: parseStoredScheduleView,
  });

  // The resolved view is deliberately not written back to the URL — doing so would make the param present from then
  // on, so the next visit would read a stamped default instead of the remembered choice.
  const view = resolveScheduleView(urlView, storedView);

  useEffect(() => {
    if (isReady && !enabled) {
      void navigate({ to: '/patients', replace: true });
    }
  }, [isReady, enabled, navigate]);

  if (!isReady || !enabled) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <Schedule
      view={view}
      setView={(next) => {
        setStoredView(next);
        // Replaced rather than pushed: a clinician sampling Day → Week shouldn't have to walk back through
        // every toggle to leave the schedule.
        void navigateHere({ search: (prev) => ({ ...prev, view: next }), replace: true });
      }}
    />
  );
};

export const Route = createFileRoute('/_auth/appointments/')({
  component: AppointmentsRoute,
  loader: () => ({ crumb: 'Appointments' }),
  validateSearch: AppointmentsSearchSchema,
});
